import { createContext, Dispatch, FC, PropsWithChildren, SetStateAction, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { AuthContextProps, useAuth } from 'react-oidc-context'
import { OpenFeature } from '@openfeature/react-sdk'

import Api from '../utils/api'
import { LOCAL_STORAGE_KEYS } from '../utils/constants'
import useSessionMonitor from '../utils/hooks/useSessionMonitor'

export const AuthenticationContext = createContext<{
  auth: AuthContextProps
  ownUser: IUserType | undefined
  setOwnUser: Dispatch<SetStateAction<IUserType | undefined>>
  isAuthenticated: boolean
  ownPermissions: IPermissionType[]
  /**
   * Handles the logout process for the user.
   * @param fromBroadcast should be true if the logout is triggered from a broadcast message
   */
  handleLogout: (fromBroadcast?: boolean) => Promise<void>
  hasInternalUserAccess: boolean
}>({
  auth: {} as AuthContextProps,
  ownUser: undefined,
  setOwnUser: () => null,
  isAuthenticated: false,
  ownPermissions: [],
  handleLogout: async () => {},
  hasInternalUserAccess: false,
})

const AuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const auth = useAuth()
  const [ownUser, setOwnUser] = useState<IUserType | undefined>(undefined)
  const [ownPermissions, setOwnPermissions] = useState<IPermissionType[]>([])
  const [hasInternalUserAccess, setHasInternalUserAccess] = useState<boolean>(false)
  const { isAuthenticated, isLoading } = auth
  const logoutChannelRef = useRef<BroadcastChannel | null>(null)
  const isLoggingOutRef = useRef(false)
  const silentRenewInFlightRef = useRef<Promise<unknown> | null>(null)
  const lastSilentRenewAtRef = useRef<number>(0)

  const handleLogout = useCallback(
    async (fromBroadcast = false) => {
      // Prevent multiple simultaneous logout attempts
      if (isLoggingOutRef.current) {
        return
      }
      isLoggingOutRef.current = true

      try {
        localStorage.setItem(LOCAL_STORAGE_KEYS.LOGGED_OUT, Date.now().toString())

        // Store current location before logout for redirect after re-login
        if (!fromBroadcast) {
          const currentLocation = window.location.pathname + window.location.search + window.location.hash
          // Only store redirect if not on login page
          if (!currentLocation.includes('/login')) {
            sessionStorage.setItem('redirectAfterLogin', currentLocation)
          }
        }

        // Post message to other tabs BEFORE redirect (which might interrupt execution)
        if (!fromBroadcast && logoutChannelRef.current) {
          logoutChannelRef.current.postMessage({ type: 'SIGN_OUT' })
        }

        setOwnUser(undefined)
        setOwnPermissions([])
        setHasInternalUserAccess(false)

        await auth.removeUser()
        await auth.revokeTokens()
        await auth.signoutRedirect()
      } catch (error) {
        // Reset flag if logout fails so user can retry
        isLoggingOutRef.current = false
        throw error
      }
    },
    [auth]
  )

  // Ping `/api/users/me` every minute to verify the session is still valid. Once the server session expires, this ping will return a 401 and the user will be logged out on the front end and redirected to the login screen.
  useSessionMonitor({
    accessToken: auth.user?.access_token,
    isAuthenticated,
    checkIntervalMs: 60000, // Check every 60 seconds
    enabled: isAuthenticated && !isLoading,
  })

  // Intercept all 401 fetch responses and log the user out only when unauthorized is persistent.
  // A single 401 can happen during token rotation, so we attempt silent renew first to avoid race-condition logouts.
  // if we got 401 again after we re-tried - then log out for good
  useEffect(() => {
    const originalFetch = window.fetch
    const POST_RENEW_401_WINDOW_MS = 5000

    window.fetch = async (...args) => {
      const response = await originalFetch(...args)

      if (
        response.status !== 401 ||
        isLoggingOutRef.current ||
        auth.activeNavigator === 'signinSilent' ||
        auth.activeNavigator === 'signinRedirect' ||
        auth.activeNavigator === 'signoutRedirect'
      ) {
        return response
      }

      const now = Date.now()
      const didRecentlyRenew = now - lastSilentRenewAtRef.current < POST_RENEW_401_WINDOW_MS

      // If we still receive 401 shortly after a successful renew, treat it as a valid unauthorized state.
      if (didRecentlyRenew) {
        await handleLogout()
        return response
      }

      // Avoid parallel renews from multiple concurrent 401 responses.
      if (silentRenewInFlightRef.current) {
        return response
      }

      try {
        silentRenewInFlightRef.current = auth.signinSilent()
        await silentRenewInFlightRef.current
        lastSilentRenewAtRef.current = Date.now()
        return response
      } catch {
        await handleLogout()
        return response
      } finally {
        silentRenewInFlightRef.current = null
      }
    }

    return () => {
      window.fetch = originalFetch
    }
  }, [auth, handleLogout])

  const getOwnUser = useCallback(
    async (isSubscribed?: boolean) => {
      if (!auth.user?.access_token) return
      try {
        const res = await Api.get('/api/users/me')

        if (res.ok) {
          setOwnUser(res.body)

          // Set feature flag context with user information after login
          const evaluationContext = {
            targetingKey: String(res.body.id),
            userId: String(res.body.id),
            roleId: String(res.body.roleId),
          }
          OpenFeature.setContext(evaluationContext)

          const resPermissions = await Api.get('/api/permissions?onlyGranted=true')
          if (resPermissions.ok) {
            const permissions = resPermissions.body
            setOwnPermissions(permissions)

            const hasInternalAccess = permissions.some((permission: IPermissionType) => permission.name === 'Internal User Access')
            setHasInternalUserAccess(hasInternalAccess)
          } else {
            handleLogout()
          }
        } else {
          handleLogout()
        }
      } catch {
        if (isSubscribed) {
          handleLogout()
        }
      }
    },
    [auth.user?.access_token, handleLogout]
  )

  // Initialize broadcast channel for cross-tab logout
  useEffect(() => {
    logoutChannelRef.current = new BroadcastChannel('logout_channel')

    return () => {
      if (logoutChannelRef.current) {
        logoutChannelRef.current.close()
        logoutChannelRef.current = null
      }
    }
  }, [])

  // Listen for OIDC session events
  useEffect(() => {
    const handleSessionExpired = () => {
      handleLogout()
    }

    const handleSilentRenewError = (event: CustomEvent) => {
      // Only logout if the error indicates session expiration
      // Some errors might be temporary network issues
      if (event.detail?.error === 'login_required' || event.detail?.message?.includes('login_required')) {
        handleLogout()
      }
    }

    window.addEventListener('oidc-session-expired', handleSessionExpired)
    window.addEventListener('oidc-silent-renew-error', handleSilentRenewError as EventListener)

    return () => {
      window.removeEventListener('oidc-session-expired', handleSessionExpired)
      window.removeEventListener('oidc-silent-renew-error', handleSilentRenewError as EventListener)
    }
  }, [handleLogout])

  useEffect(() => {
    let isSubscribed = true

    // reset ownUser on logout
    if (!isAuthenticated && ownUser) {
      setOwnUser(undefined)
    }

    if (isAuthenticated && !ownUser && !isLoading) {
      getOwnUser(isSubscribed)
    }

    return () => {
      isSubscribed = false
    }
  }, [auth.user?.access_token, getOwnUser, handleLogout, isAuthenticated, isLoading, ownUser])

  useEffect(() => {
    const handleBroadcastMessage = (event: MessageEvent) => {
      if (event.data.type === 'SIGN_OUT') {
        // Log out of this tab without sending another broadcast message
        handleLogout(true)
      }
    }

    if (logoutChannelRef.current) {
      logoutChannelRef.current.addEventListener('message', handleBroadcastMessage)
    }

    return () => {
      if (logoutChannelRef.current) {
        logoutChannelRef.current.removeEventListener('message', handleBroadcastMessage)
      }
    }
  }, [handleLogout])

  return (
    <AuthenticationContext.Provider
      value={{
        auth,
        ownUser,
        setOwnUser,
        isAuthenticated,
        ownPermissions,
        handleLogout,
        hasInternalUserAccess,
      }}
    >
      {children}
    </AuthenticationContext.Provider>
  )
}

export default AuthProvider

export const useAuthentication = () => useContext(AuthenticationContext)
